// Reusable file card component
import { useState } from 'react';

const FileCard = ({ file, showPreview = false }) => {
  const [previewExpanded, setPreviewExpanded] = useState(false);
  
  // Helper functions for displaying file information
  const getFileTypeLabel = (type) => {
    const types = {
      'html': 'HTML',
      'reduced-level1-html': 'Reduced HTML (L1)',
      'reduced-level2-html': 'Reduced HTML (L2)',
      'screenshot': 'Screenshot',
      'audio': 'Audio',
      'video': 'Video',
    };
    
    return types[type] || type;
  };
  
  const getFileIcon = (type) => {
    switch (type) {
      case 'html':
      case 'reduced-level1-html':
      case 'reduced-level2-html':
        return (
          <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        );
      case 'screenshot':
        return (
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
        );
    }
  };
  
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderPreview = () => {
    if (!showPreview || !file.downloadUrl) return null;
    
    if (file.type === 'screenshot') {
      return (
        <div className="mt-4">
          <img 
            src={file.downloadUrl} 
            alt={file.filename} 
            className="w-full h-auto rounded border border-gray-200"
          />
        </div>
      );
    }
    
    if (file.type && file.type.includes('html')) {
      return (
        <div className="mt-4">
          <button
            onClick={() => setPreviewExpanded(!previewExpanded)}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            {previewExpanded ? 'Collapse Preview' : 'Expand Preview'}
          </button>
          
          {previewExpanded && (
            <div className="border border-gray-200 rounded">
              <iframe
                src={file.downloadUrl}
                title={file.filename}
                className="w-full h-96 rounded"
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">{getFileIcon(file.type)}</div>
        <div className="ml-4 flex-1">
          <h3 className="text-lg font-medium text-gray-900 truncate" title={file.filename}>
            {file.filename}
          </h3>
          <div className="mt-1 flex items-center text-sm text-gray-500">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
              {getFileTypeLabel(file.type)}
            </span>
            {file.size && (
              <span className="ml-2">{formatFileSize(file.size)}</span>
            )}
            {file.url && (
              <span className="ml-2 truncate max-w-xs" title={file.url}>
                {file.url}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Created: {formatDate(file.createdAt)}
          </p>
          <div className="mt-2">
            {file.downloadUrl && (
              <a
                href={file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Download File
              </a>
            )}
          </div>
        </div>
      </div>
      
      {renderPreview()}
    </div>
  );
};

export default FileCard;